// @ts-strict-ignore
import type { FieldFunctionOptions, FieldPolicy, Reference } from "@apollo/client";
import type { ReadFieldFunction, SafeReadonly } from "@apollo/client/cache/core/types/common";

type KeyArgs = FieldPolicy<any>["keyArgs"];

type TEdge<TNode> =
  | {
      cursor?: string;
      node: TNode;
    }
  | (Reference & { cursor?: string });

type TPageInfo = {
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  startCursor: string;
  endCursor: string;
};

type TConnection<TNode> = {
  edges?: TEdge<TNode>[];
  pageInfo?: TPageInfo;
  nodes?: TNode[];
  totalCount?: number;
};

type TExistingConnection<TNode> = Readonly<TConnection<TNode>>;

type TIncomingConnection<TNode> = TConnection<TNode>;

type ConnectionFieldPolicy<TNode> = FieldPolicy<
  TExistingConnection<TNode>,
  TIncomingConnection<TNode>,
  TIncomingConnection<TNode>
>;

const getIncomingEdges = <TNode = Reference>(
  incoming: SafeReadonly<TIncomingConnection<TNode>>,
  isReference: (obj: any) => obj is Reference,
  readField: ReadFieldFunction
): TEdge<TNode>[] => {
  const incomingEdges = incoming?.edges?.map(edge => {
    if (isReference(edge)) {
      // In case edge is a Reference, we read out its cursor field and
      // store it as an extra property of the Reference object.
      return {
        ...edge,
        cursor: readField<string>("cursor", edge),
      };
    }

    return edge;
  });

  return incomingEdges ?? [];
};

/**
 * This has been for the most part copied over from:
 * https://github.com/apollographql/apollo-client/blob/main/src/utilities/policies/pagination.ts
 *
 * It supports merging edges for pagination purposes. Not currently used anywhere but left intact in case of future use.
 */
const mergeEdges = <TNode = Reference>(
  existing: SafeReadonly<TExistingConnection<TNode>>,
  incoming: SafeReadonly<TIncomingConnection<TNode>>,
  { args, isReference, readField }: FieldFunctionOptions
) => {
  const incomingEdges = getIncomingEdges(incoming, isReference, readField);

  if (incoming.pageInfo) {
    const { pageInfo } = incoming;
    const { startCursor, endCursor } = pageInfo;
    const firstEdge = incomingEdges?.[0];
    const lastEdge = incomingEdges?.[incomingEdges.length - 1];
    // In case we did not request the cursor field for edges in this
    // query, we can still infer cursors from pageInfo.
    if (firstEdge && startCursor) {
      firstEdge.cursor = startCursor;
    }
    if (lastEdge && endCursor) {
      lastEdge.cursor = endCursor;
    }
  }

  let prefix = existing.edges || [];
  let suffix: typeof prefix = [];

  if (args?.after) {
    // This comparison does not need to use readField("cursor", edge),
    // because we stored the cursor field of any Reference edges as an
    // extra property of the Reference object.
    const index = prefix.findIndex(edge => edge.cursor === args.after);
    if (index >= 0) {
      prefix = prefix.slice(0, index + 1);
    }
  } else if (args?.before) {
    const index = prefix.findIndex(edge => edge.cursor === args.before);
    suffix = index < 0 ? prefix : prefix.slice(index);
  }

  return [...prefix, ...incomingEdges, ...suffix];
};

const mergeNodes = <TNode = Reference>(
  existing: SafeReadonly<TExistingConnection<TNode>>,
  incoming: SafeReadonly<TIncomingConnection<TNode>>,
  { args }: FieldFunctionOptions
) => {
  // This is for the case of refetch
  // We don't want to merge incoming and existing on a refetch
  // It will cause duplicate entries in the cache
  // Instead we assume that we want incoming and don't care about the cache
  if (!args?.after) return incoming?.nodes;

  if (existing?.nodes && incoming?.nodes) {
    return [...existing?.nodes, ...incoming?.nodes];
  }

  return existing?.nodes || incoming?.nodes || [];
};

const connectionAppendPagination = <TNode = Reference>(
  keyArgs: KeyArgs = false
): ConnectionFieldPolicy<TNode> => {
  return {
    keyArgs,
    merge: (existing, incoming, options) => {
      if (!existing) return incoming;

      const pageInfo: TPageInfo = {
        ...existing.pageInfo,
        ...incoming.pageInfo,
      };

      return {
        ...existing,
        ...incoming,
        edges: mergeEdges(existing, incoming, options),
        pageInfo,
        nodes: mergeNodes(existing, incoming, options),
      };
    },
  };
};

export { connectionAppendPagination };
export type { TIncomingConnection, TExistingConnection };
